import {Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { ActivityType, Countries, TalentProfilePage } from "../../../enums";
import { FormBuilder, FormGroup } from "@angular/forms";
import { TalentProfileApiService } from "../talent-profile-api.service";
import { PageService } from "../shared/page-service";
import { ActivatedRoute, Router } from "@angular/router";
import { CountryInfo, TalentEmploymentAuthorizationRequest } from "../../../../typings";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { TranslateService } from "@ngx-translate/core";
import { NOT_A_FIT, sendDoThisLaterEmail } from "../../shared/util/talent-happy-path-util";

@Component({
    selector: 'app-employment-authorization',
    templateUrl: './employment-authorization.component.html',
    styleUrl: './employment-authorization.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EmploymentAuthorizationComponent implements OnInit {
  @ViewChild('locationSearch') locationSearchElementRef: ElementRef;
  authorizationForm: FormGroup;
  moatsLocation: { [k: string]: any } = { homeLocation: '', latitude: 0, longitude: 0 };
  postingId: number;
  country: CountryInfo;
  countryName: string;
  talent: Talent;
  posting: PostingInfo;
  isModalVisible: boolean = false;

  @Input() fromNotAFit: boolean = false;

  constructor(
    private fb: FormBuilder,
    private talentProfileApiService: TalentProfileApiService,
    private pageService: PageService,
    private route: ActivatedRoute,
    private router: Router,
    private talentHappyPathDataService: TalentHappyPathDataService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.authorizationForm = this.fb.group({
      workAuthorization: '',
      requireImmigrationSponsorship: ''
    });

    this.talent = this.talentHappyPathDataService.talent;
    this.posting = this.talentHappyPathDataService.postingInfo;
    this.postingId = this.posting?.jobPostingId;
    this.loadCountry();
    const savedAuthorization = this.talentHappyPathDataService.employmentAuthorization;
    if (savedAuthorization?.talentId === this.talent?.personId && savedAuthorization?.countryId === this.country.countryId) {
      this.authorizationForm.patchValue({
        workAuthorization: savedAuthorization.authorizedToWork,
        requireImmigrationSponsorship: savedAuthorization.requiresSponsorship
      }, { emitEvent: false });
    }
    this.authorizationForm.valueChanges.subscribe((values) => {
      this.saveTalentAuthorization(values);
  });
  }
  loadCountry() {
    let countryId;
    if (this.talentHappyPathDataService.isGeneralApplication) {
      countryId = this.talent.countryId;
    } else {
      countryId = this.posting.countryId;
    }
    this.countryName = this.translate.instant(`talent-happy-path.country-names.${countryId}`);
    this.country = {
      countryId: countryId,
      countryName: this.countryName
    };
  }
  saveTalentAuthorization(talentAuthorization) {
      let request: TalentEmploymentAuthorizationRequest =
        {
          talentId: this.talent.personId,
          authorizedToWork: talentAuthorization.workAuthorization ? true:false,
          countryId: this.country.countryId,
          requiresSponsorship: talentAuthorization.requireImmigrationSponsorship? true:false
        }

        this.talentProfileApiService.saveEmploymentAuthorization(request).subscribe(response => {
          console.log('Authorization saved');
        }, error => {
          console.error('Error saving Authorization', error);
        });
  }

  goToEmploymentSalary() {
    this.pageService.setActivePage(TalentProfilePage.EMPLOYMENT_SALARY);
  }

  goToTalentEeocOrThankYou() {
    switch (this.country.countryId) {
      // Countries that skip the demographics step during the application flow.
      // This intentionally diverges from isEeocFormCountry(Integer countryId) in
      // utility/src/main/java/com/aquent/utility/geo/CountryUtility.java: US and AU are still
      // EEOC-form countries there, but collecting demographics pre-hire is not compliant, so it
      // is suppressed here (US, AU). DE is not an EEOC-form country at all.
      case Countries.USA:
      case Countries.DE:
      case Countries.AU:
        this.saveFinishedProfileActivity();
        break;
      case Countries.UK:
      case Countries.NL:
      case Countries.CA:
        this.pageService.setActivePage(TalentProfilePage.TALENT_EEOC);
        break;
      default:
        this.saveFinishedProfileActivity();
        break;
    }
  }

  saveFinishedProfileActivity() {
    this.talentProfileApiService.createTalentProfileActivity(
      ActivityType.TALENT_PROFILE_MODULE_FINISHED,
      this.talent.personId,
      this.posting?.orderId,
      this.posting?.jobPostingId
    ).subscribe(
      () => {
        if (this.fromNotAFit) {
          this.router.navigate(['/thank-you'], { queryParams: { from: NOT_A_FIT }, skipLocationChange: true });
        } else {
          this.router.navigateByUrl('/thank-you', { skipLocationChange: true });
        }
      },
      error => console.error('Error saving profile finished activity', error)
    );
  }

  doThisLater() {
    if (!this.talentHappyPathDataService.doThisLaterEmailSent) {
      sendDoThisLaterEmail(
        this.posting?.jobPostingId ?? null,
        this.talent.personId,
        this.talent.firstName,
        this.talent.lastName,
        this.talent.email,
        this.posting?.jobTitle ?? null,
        this.posting?.orderId ?? null,
        this.talentProfileApiService,
        this.talentHappyPathDataService.isGeneralApplication
      )
    }
  }
}
